import { Alert } from "@jugl-web/ui-components";
import { useClipboard, useToast, useTranslations } from "@jugl-web/utils";
import React, { useCallback, useState } from "react";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { environment } from "@web-src/environments/environment";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ReactComponent as LiveChatIcon } from "./assets/live-chat.svg";
import { ReactComponent as LiveChatBorderedIcon } from "./assets/live-chat-2.svg";
import defaultAvatar from "./assets/default-avatar.png";
import { ReactComponent as ExternalIcon } from "./assets/external.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

interface LiveChatAppAccountCardProps {
  variant?: "card" | "tile";
  isClickable?: boolean;
}

const skipUrlProtocol = (url: string) => url.replace(/^https?:\/\//, "");

export const LiveChatAppAccountCard: React.FC<LiveChatAppAccountCardProps> = ({
  variant,
  isClickable,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { copyToClipboard } = useClipboard();
  const { entity, entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [
    updateLiveChatAppConfig,
    { isLoading: isUpdateLiveChatAppConfigLoading },
  ] = integrationsApi.useUpdateLiveChatAppConfigMutation();

  const { crmConfig } = useManageAccountsDrawer();
  const [isDisableLiveChatAlertOpen, setIsDisableLiveChatAlertOpen] =
    useState(false);

  const liveChatAppAccount = crmConfig?.livechat;

  const toggleLiveChatAppAccount = useCallback(
    async (enabled: boolean) => {
      if (!liveChatAppAccount) return;

      const resp = await updateLiveChatAppConfig({
        entityId,
        enabled,
      });

      if (resp && "data" in resp) {
        toast(
          enabled
            ? t({
                id: "client-support-page.live-chat-via-link-is-enabled",
                defaultMessage: "Live Chat via Link is enabled",
              })
            : t({
                id: "client-support-page.live-chat-via-link-is-disabled",
                defaultMessage: "Live Chat via Link is disabled",
              })
        );
        return;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    },
    [entityId, liveChatAppAccount, t, toast, updateLiveChatAppConfig]
  );

  const liveChatUrl = `${environment.liveChatUrl}/${entity.uniqueId}`;

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable || !liveChatAppAccount) {
        return;
      }

      transitionTo({ name: "liveChatAppAccount" });
    };

    return (
      <CRMAccountTile
        title={t({
          id: "client-support-page.live-chat-via-link",
          defaultMessage: "Live Chat via Link",
        })}
        isConnected={!!liveChatAppAccount}
        onClick={handleClick}
        icon={LiveChatBorderedIcon}
        description={
          liveChatAppAccount
            ? liveChatAppAccount.enabled
              ? t({
                  id: "client-support-page.live-chat-via-link-is-enabled-tile",
                  defaultMessage: "Live Chat via Link is live",
                })
              : t({
                  id: "client-support-page.live-chat-via-link-is-disabled-tile",
                  defaultMessage: "Live Chat via Link is disabled",
                })
            : t({
                id: "client-support-page.send-link-to-customer-start-chatting",
                defaultMessage:
                  "Send a link to customer and start chatting in Browser Live Chat",
              })
        }
        img={entity.logoImg || undefined}
        defaultAvatar={defaultAvatar}
        status={liveChatAppAccount?.enabled ? "enabled" : "disabled"}
      />
    );
  }

  return (
    <>
      <CRMAccountCard
        title={t({
          id: "client-support-page.live-chat-via-link",
          defaultMessage: "Live Chat via Link",
        })}
        onClick={
          isClickable
            ? () => transitionTo({ name: "liveChatAppAccount" })
            : undefined
        }
        icon={LiveChatIcon}
        description={t({
          id: "client-support-page.send-link-to-customer-start-conversation",
          defaultMessage: "Send a link to customer and start conversation",
        })}
        status={liveChatAppAccount?.enabled === true ? "enabled" : "disabled"}
        switchProps={{
          isChecked: liveChatAppAccount?.enabled === true,
          onChange: (isChecked) => {
            if (isChecked) {
              toggleLiveChatAppAccount(true);
              return;
            }

            setIsDisableLiveChatAlertOpen(true);
          },
          isDisabled: isUpdateLiveChatAppConfigLoading,
        }}
      >
        <div className="flex flex-col gap-6">
          <div className="h-px w-full bg-[#F2F2F2]" />
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-lg font-medium text-black">
              {t({
                id: "client-support-page.live-chat-test-it-out",
                defaultMessage: "Test it out:",
              })}
            </span>
            <span className="font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800">
              {t({
                id: "client-support-page.visit-live-chat-for-full-experience",
                defaultMessage:
                  "Visit your Live Chat to get full experience ✨",
              })}
            </span>
          </div>
          <div className="flex w-[500px] items-center justify-between rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4">
            <span className="cursor-text font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark select-text">
              {skipUrlProtocol(liveChatUrl)}
            </span>
            <div className="flex items-center gap-3">
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-300 bg-white ring-grey-300 transition ring-inset hover:ring-1"
                    ref={ref}
                    onClick={() => window.open(liveChatUrl, "_blank")}
                    {...props}
                  >
                    <ExternalIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "client-support-page.visit-page",
                  defaultMessage: "Visit Page",
                })}
              </Tooltip>
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-grey-300 bg-white ring-grey-300 transition ring-inset hover:ring-1"
                    ref={ref}
                    onClick={() => copyToClipboard(liveChatUrl)}
                    {...props}
                  >
                    <CopyIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "client-support-page.copy-live-chat-link",
                  defaultMessage: "Copy Live Chat Link",
                })}
              </Tooltip>
            </div>
          </div>
        </div>
      </CRMAccountCard>
      <Alert
        isOpen={isDisableLiveChatAlertOpen}
        onRequestClose={() => setIsDisableLiveChatAlertOpen(false)}
        title={t({
          id: "client-support-page.disable-live-chat-title",
          defaultMessage: "Disable Live Chat",
        })}
        content={t({
          id: "client-support-page.disable-live-chat-description",
          defaultMessage:
            "If you will disable the Live Chat, Customer will no longer be able to reach out to you via shared Chat Link",
        })}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            style: { flex: "none", width: "140px" },
          },
          {
            text: t({
              id: "common.disable",
              defaultMessage: "Disable",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await toggleLiveChatAppAccount(false);
            },
          },
        ]}
      />
    </>
  );
};
